/**
 * IntakeSelection Page - Choose between Quick Intake and Smart Intake
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, Brain, ArrowRight, Clock, CheckCircle, FileText } from 'lucide-react';
import SmartIntakeWizard from '@/components/SmartIntakeWizard';

const IntakeSelection = () => {
  const navigate = useNavigate();
  const [showSmartIntake, setShowSmartIntake] = useState(false);

  const handleQuickIntake = () => {
    navigate('/new-procurement');
  };

  const handleSmartIntakeComplete = (procurementData) => {
    // Navigate to continue procurement with the created procurement ID
    navigate(`/continue-procurement/${procurementData.procurement_id}`);
  };

  if (showSmartIntake) {
    return (
      <Layout>
        <SmartIntakeWizard 
          onComplete={handleSmartIntakeComplete}
          onCancel={() => setShowSmartIntake(false)}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-12 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-zinc-900 mb-3">
            Start New Procurement
          </h1>
          <p className="text-zinc-600 max-w-2xl mx-auto">
            Choose your preferred intake method. Smart Intake uses AI to analyze your requirement and extract key information automatically.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Quick Intake Card - default/recommended option for beta */}
          <Card className="relative overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group border-[#0055FF]/20" onClick={handleQuickIntake}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#0055FF]/10 to-transparent rounded-bl-full" />
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 bg-[#0055FF] text-white text-xs font-medium rounded-full">
                RECOMMENDED
              </span>
            </div>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-zinc-100 rounded-xl group-hover:bg-zinc-200 transition-colors">
                  <Zap className="w-6 h-6 text-zinc-700" />
                </div>
                <div>
                  <CardTitle className="text-xl">Quick Intake</CardTitle>
                  <CardDescription>Traditional step-by-step process</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <Clock className="w-4 h-4 mt-0.5 text-zinc-400" />
                  <span>Enter requirement, upload documents, answer questions sequentially</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-zinc-400" />
                  <span>Full control over each step</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <FileText className="w-4 h-4 mt-0.5 text-zinc-400" />
                  <span>Works for all procurement categories</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full group-hover:bg-zinc-100">
                Start Quick Intake
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Smart Intake Card - disabled for beta, kept in place for easy re-enable later */}
          <Card className="relative overflow-hidden opacity-50 cursor-not-allowed border-zinc-200">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-zinc-100 to-transparent rounded-bl-full" />
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 bg-zinc-500 text-white text-xs font-medium rounded-full">
                COMING SOON
              </span>
            </div>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-[#0055FF]/10 rounded-xl">
                  <Brain className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <CardTitle className="text-xl">Smart Intake</CardTitle>
                  <CardDescription>AI-powered requirement analysis</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <Brain className="w-4 h-4 mt-0.5 text-[#0055FF]" />
                  <span>Specialized IT & Facilities buyer agents</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-[#0055FF]" />
                  <span>Automatic extraction from text and documents</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-zinc-600">
                  <FileText className="w-4 h-4 mt-0.5 text-[#0055FF]" />
                  <span>Gap analysis shows FOUND / INFERRED / MISSING</span>
                </li>
              </ul>
              <Button
                className="w-full"
                disabled
                title="Specialized AI agents coming soon"
                data-testid="smart-intake-disabled-btn"
              >
                Start Smart Intake
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-zinc-500 text-center mt-2">
                Specialized AI agents coming soon
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Info Banner */}
        <div className="mt-8 p-4 bg-zinc-50 rounded-lg border border-zinc-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg border border-zinc-200">
              <Brain className="w-5 h-5 text-[#0055FF]" />
            </div>
            <div>
              <p className="font-medium text-zinc-900">About Smart Intake</p>
              <p className="text-sm text-zinc-600 mt-1">
                Smart Intake uses specialized AI buyer agents for IT and Facilities procurement. 
                It reads your requirement and any attached documents, extracts relevant information, 
                and shows you exactly what was found, what was inferred, and what&apos;s missing — 
                so you only answer the questions that matter.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IntakeSelection;
